/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CANCEL, SAVE } from '@touchplan/strings';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GroupedTicket, CalendarWorkdays } from '@touchplan/ticket';
import { Priority, ProjectMember, Role, TicketType, Location, Variance } from '@touchplan/common';
import { TicketFormManager } from '../../services/ticket-form-manager/ticket-form-manager.service';
import { TicketTypeUpdateStatus } from 'src/app/app-data/models/ticket-type/TicketTypeExtensions';
import { TicketTypeCombined } from '../../../../app-data/models/ticket-type/TicketTypeExtensions';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { PlatformConfiguration } from 'src/app/services/platform-configuration/platform-configuration.service';
import { TouchplanPlatform } from 'src/app/services/platform-configuration/touchplan-platforms.enum';

export interface ITicketEditUpdateStatusDialogData {
	title?: string;
	saveButtonText?: string;
	cancelButtonText?: string;
	ticket$?: Observable<GroupedTicket>;
	roleList$?: Observable<Role[]>;
	locationList$?: Observable<Location[]>;
	projectMembers$?: Observable<ProjectMember[]>;
	priorityList$?: Observable<Priority[]>;
	varianceList$?: Observable<Variance[]>;
	editDialogFormGroupChange$?: Subject<IUpdateFields>;
	isDisabled?: boolean;
	calendarWorkdays: CalendarWorkdays;
	activeLineDate$?: Observable<string>;
}

export interface IUpdateFields {
	[key: string]: string | number | boolean[];
}

@Component({
	selector: 'ticket-edit-dialog',
	templateUrl: './ticket-edit-dialog.component.html',
	styleUrls: ['./ticket-edit-dialog.component.scss'],
	providers: [TicketFormManager],
	changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * Generic Material dialog with customizable title, content and yes/no button texts
 * Use afterClosed() observable to response to yes click, Yes emits true, and No emits false, e.g. where this dialog is opened from:
 * dialogRef.afterClosed().pipe( filter(data => data), takeUntil(this.destroy$) ).subscribe( => { ... });
 */
export class TicketEditDialogComponent implements OnInit, OnDestroy {
	public editFormGroup: UntypedFormGroup;
	public saveOrCloseButtonText = SAVE;
	public cancelButtonText = CANCEL;
	public ticketType: TicketTypeCombined;
	public onDesktop: boolean;

	private shutdownSubject$: Subject<boolean> = new Subject();

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ITicketEditUpdateStatusDialogData,
		public platformConfig: PlatformConfiguration,
		public dialogRef: MatDialogRef<TicketEditDialogComponent>,
		public ticketFormManager: TicketFormManager,
		protected formBuilder: UntypedFormBuilder
	) {
		this.onDesktop = this.platformConfig.platform === TouchplanPlatform.Desktop;
	}

	public ngOnInit() {
		this.saveOrCloseButtonText = this.data.saveButtonText || this.saveOrCloseButtonText;
		this.data.ticket$.pipe(takeUntil(this.shutdownSubject$)).subscribe((ticket) => {
			if (ticket) {
				switch ((ticket.view.type as TicketType) || undefined) {
					case TicketType.Task:
						this.ticketType = this.onDesktop
							? TicketTypeUpdateStatus.TaskUpdateStatusDialogForDesktop
							: TicketTypeUpdateStatus.TaskUpdateStatusDialogForMobile;
						break;
					case TicketType.Milestone:
						this.ticketType = this.onDesktop
							? TicketTypeUpdateStatus.MilestoneUpdateStatusDialogForDesktop
							: TicketTypeUpdateStatus.MilestoneUpdateStatusDialogForMobile;
						break;
					case TicketType.Constraint:
						this.ticketType = TicketTypeUpdateStatus.ConstraintUpdateStatusDialog;
						break;
					default:
						this.ticketType = this.onDesktop
							? TicketTypeUpdateStatus.TaskUpdateStatusDialogForDesktop
							: TicketTypeUpdateStatus.TaskUpdateStatusDialogForMobile;
						break;
				}
				// create the form the first time (this if executes once)
				if (!this.editFormGroup) {
					this.editFormGroup = this.ticketFormManager.create(this.ticketType, ticket, this.data.isDisabled);

					// Subscribing to changes and emitting them back to docked-ticket-container
					this.ticketFormManager.valueChanges
						.pipe(takeUntil(this.shutdownSubject$))
						.subscribe(subjectSubscribeWithoutComplete(this.data.editDialogFormGroupChange$));
				}
				//and then just apply updates
				else {
					this.ticketFormManager.patchForm(ticket);
				}
			}
		});
	}

	public onSave() {
		this.dialogRef.close(true);
	}

	// When cancelling changes, we return values that were on the form before changes.
	public onCancel() {
		//reverting is the responsibility of the caller
		this.dialogRef.close(false);
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.complete();
	}
}
